import {
  getDateStringInTestSeriesHeader,
  getEducatorsString,
  getEnrollmentMessage,
  getProgrammeTypeLabel,
  isPastTest
} from '@cont/SeeAll/utils';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { pluralize } from '@utils/textHelper';
import Typography from 'aquilla/core/atoms/Typography';
import Button, { BounceLoader } from 'aquilla/core/molecules/Button';
import React from 'react';
import { unlockFreePlanFor } from '@constants/index';
import IconButton from 'aquilla/core/molecules/IconButton';
import Forward from 'aquilla/icons/actions/Forward';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import Dialog from '@comp/Dialog/Dialog';
import pushRoute from '@utils/pushRoute';
import LiteUpgradeDialog from '@cont/Course/CourseHeader/LiteUpgradeDialog';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import { noOp } from '@constants/empty';
import useMounted from '@hooks/useMounted';
import { useIsMobile } from '@stores/AppCommonData';
import { useRouter } from 'next/router';
import Skeleton from 'aquilla/core/atoms/Skeleton';
import SessionCancelled from 'aquilla/icons/unacademyStore/SessionCancelled';
import SubscriptionBenefits from 'aquilla/icons/unacademyStore/SubscriptionBenefits';
import useTestSeriesInfo from './useTestSeriesInfo';
import SkuPricingDetails from './SkuPricingDetails';
import useTestSeriesActions from './useTestSeriesActions';
import useSkuDetails from './useSkuDetails';
import useTestSeriesEvents from './useTestSeriesEvents';

const Container = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
  ${({ $isSticky }) => {
    if ($isSticky) {
      return `
        border: 1px solid var(--color-divider);
        border-radius: ${Spacings.SPACING_2B};
        padding: ${Spacings.SPACING_4B};
        height: max-content;
        position: sticky;
        top: 168px;
      `;
    }
  }}

  ${({ $isHeaderShown }) => {
    if ($isHeaderShown) {
      return `
        opacity: 1;
        user-select: auto;
      `;
    }
    return `
      opacity: 0;
      z-index: -1;
      user-select: none;
    `;
  }} 
  transition: opacity 0.2s ease-in-out;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const Title = styled(Typography)`
  margin-top: ${Spacings.SPACING_3B};
  max-width: 512px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_6};
  }
`;

const Type = styled(Typography)`
  color: var(--color-i-blue-25);
  font-weight: ${Fonts.HEAVY};
  line-height: ${Spacings.SPACING_3B};

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${({ $isSticky }) => ($isSticky ? '244px' : '300px')};
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const EnrollWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${mediaQuery.uptoMobile} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    width: 100%;
    background-color: var(--color-base-0);
    padding: ${Spacings.SPACING_4B};
    border-top: 1px solid var(--color-divider);
  }
`;

const FlexRowWithMargin = styled(FlexRow)`
  margin-top: ${Spacings.SPACING_4B};
`;

const IconWrapper = styled.div`
  margin-right: ${Spacings.SPACING_3B};
`;

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_4B};
`;

const EnrollActionBtn = styled(Button)`
  width: ${({ $isSticky }) => ($isSticky ? '100%' : '214px')};
  height: ${Spacings.SPACING_12B};
  margin-top: ${({ $isSticky }) =>
    $isSticky ? `${Spacings.SPACING_4B}` : `${Spacings.SPACING_6B}`};
  .button--right-icon {
    height: ${Spacings.SPACING_6B};
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: 0;
  }
`;

const StyledP3 = styled(Typography)`
  margin-bottom: ${Spacings.SPACING_1B};
`;

const ShareIconButton = styled(IconButton)`
  height: ${Spacings.SPACING_12B};
  width: 48px;
  margin-top: ${Spacings.SPACING_6B};
  margin-left: ${Spacings.SPACING_4B};
`;

const StyledTextSkeleton = styled(Skeleton)`
  &.skeleton {
    margin-bottom: ${Spacings.SPACING_1B};
  }
`;

const TestSeriesDetails = ({
  isSticky,
  fromStore,
  isHeaderShown,
  onOpenCbtVenueSelection
}) => {
  const { data } = useTestSeriesInfo();
  const {
    name,
    programmeSubtype,
    programmeType,
    authors = [],
    goal = {},
    isPartOfBatch,
    uid,
    startsAt,
    endsAt
  } = data;
  const { slug: goalSlug, uid: goalUid } = goal;
  const { query } = useRouter();
  const { isValidating, isSkuAvailable, availableSku, hasSkuAccess } =
    useSkuDetails(goalUid, query.courseUID);
  const isMobile = useIsMobile();
  const loadingLabel = <BounceLoader />;

  const {
    getButtonLabel,
    getButtonVariant,
    handleShare,
    handleEnroll,
    getIcon,
    acceptUnenrollClick,
    closeLiteUpgradeDialog,
    freePlanUnlock,
    closeShareOptions,
    onCloseUnenrollModal,
    closeFreePlanUnlockDrawer,
    showLoader,
    showShareOptions,
    unEnrollModalOpen,
    freePlanUnlockDrawerState,
    showLiteUpgradeDialog
  } = useTestSeriesActions();

  const { sendShareEvent } = useTestSeriesEvents(hasSkuAccess);

  const isMounted = useMounted();

  const getTestScheduleLabel = () => {
    if (fromStore) {
      if (!hasSkuAccess && isSkuAvailable && availableSku?.expiresAt) {
        return {
          text: `${getEnrollmentMessage(availableSku.expiresAt, true)}`,
          color: 'var(--color-i-red)'
        };
      }
    }

    return { text: 'Test schedule', color: 'var(--color-text-secondary)' };
  };

  const handleShareWithEvent = () => {
    handleShare();
    sendShareEvent();
  };

  const renderEnrollSection = () => {
    return (
      <EnrollWrapper>
        {isMounted && (
          <>
            <EnrollActionBtn
              $isSticky={isSticky}
              size="large"
              isEducatorSide
              variant={showLoader ? 'secondary' : getButtonVariant()}
              label={showLoader ? loadingLabel : getButtonLabel()}
              startIcon={!showLoader && getIcon()}
              onClick={showLoader ? noOp : handleEnroll}
              disabled={showLoader}
            />
            {!isSticky && !isMobile && (
              <ShareIconButton
                variant="secondary"
                onClick={handleShareWithEvent}
              >
                <Forward />
              </ShareIconButton>
            )}
          </>
        )}
      </EnrollWrapper>
    );
  };

  const isLoadingInStore = fromStore && isValidating;

  const renderActions = () => {
    if (fromStore) {
      return (
        <SkuPricingDetails
          isSticky={isSticky}
          handleShare={handleShareWithEvent}
          goalUid={goalUid}
          onOpenCbtVenueSelection={onOpenCbtVenueSelection}
        />
      );
    }
    return renderEnrollSection();
  };

  const upgradeSubscription = (e) => {
    e.stopPropagation();
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${goalSlug}/${goalUid}/subscribe`,
      {},
      'course'
    );
  };

  const renderLiteUpgradeDialog = () => {
    if (isPartOfBatch) {
      return (
        <LiteUpgradeDialog
          onClose={closeLiteUpgradeDialog}
          upgradeSubscription={upgradeSubscription}
          message="Plus & Iconic learners can enroll into this test series. Also get access to notes and more self-study features."
        />
      );
    }

    return (
      <LiteUpgradeDialog
        onClose={closeLiteUpgradeDialog}
        upgradeSubscription={upgradeSubscription}
      />
    );
  };
  return (
    <Container $isSticky={isSticky} $isHeaderShown={isHeaderShown}>
      <Type variant="p3">
        {getProgrammeTypeLabel(programmeSubtype, programmeType)}
      </Type>
      <Title as="h1" variant={isSticky || isMobile ? 'h4' : 'h2'}>
        {name}
      </Title>
      <Wrapper $isSticky={isSticky}>
        {authors?.length > 0 && (
          <FlexRowWithMargin>
            <IconWrapper>
              <SubscriptionBenefits width={48} height={48} />
            </IconWrapper>
            <FlexColumn $isSticky={isSticky}>
              <StyledP3 variant="p3" color="var(--color-text-secondary)">
                {pluralize(authors.length, 'Educator', 's', true)}
              </StyledP3>
              <Typography variant="p3" color="var(--color-text-primary)">
                {getEducatorsString(authors)}
              </Typography>
            </FlexColumn>
          </FlexRowWithMargin>
        )}
        {(!fromStore || (fromStore && !isPastTest(endsAt))) && (
          <FlexRowWithMargin>
            <IconWrapper>
              <SessionCancelled width={48} height={48} />
            </IconWrapper>
            <FlexColumn>
              {isLoadingInStore ? (
                <StyledTextSkeleton variant="text" width={100} height={10} />
              ) : (
                <StyledP3 variant="p3" color={getTestScheduleLabel().color}>
                  {getTestScheduleLabel().text}
                </StyledP3>
              )}
              <Typography variant="p3" color="var(--color-text-primary)">
                {getDateStringInTestSeriesHeader(startsAt, endsAt)}
              </Typography>
            </FlexColumn>
          </FlexRowWithMargin>
        )}
      </Wrapper>

      {renderActions()}
      <Dialog onClose={closeShareOptions} dialogState={showShareOptions}>
        <ShareDialog data={data} useDataLink />
      </Dialog>
      <Dialog
        onClose={onCloseUnenrollModal}
        dialogState={unEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message="This test-series will be removed from your schedule and you will stop receiving updates related to this course"
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptUnenrollClick}
        rejectOnClick={onCloseUnenrollModal}
      />
      {showLiteUpgradeDialog && renderLiteUpgradeDialog()}

      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={unlockFreePlanFor.COURSE}
        objectUid={uid}
        callBack={freePlanUnlock}
      />
    </Container>
  );
};

export default React.memo(TestSeriesDetails);
